/**
 * Loads a game module and validates the index it ships (DATA-037), for any
 * screen reached with a `gameId` that has already passed `isKnownGame` --
 * `LevelSelect` and `Play` both need this, and both must tell "the chunk
 * would not download" apart from "the id does not exist" (SHELL-013 is the
 * caller's job, not this hook's).
 *
 * There is no index fetch to speak of: `GameModule.levels` ships inside the
 * chunk, so `DATA-030` (load the index before level select) and `DATA-045`
 * (a game's index failing to load gets an error with retry) are satisfied by
 * the dynamic `import()` itself and by validating what it resolves to, not by
 * a second network request. See TAD-001.
 */
import { useEffect, useState } from 'preact/hooks';

import type { GameModule } from '@contracts/game-module';
import type { LevelIndex } from '@contracts/level';
import type { LoadCause, LoadFailure } from '@platform/levels';
import { validateGameIndex } from '@platform/levels';
import { loadGame } from '@platform/game-registry';

export type GameModuleState =
  | { status: 'loading' }
  | { status: 'ready'; game: GameModule; index: LevelIndex }
  | { status: 'error'; failure: LoadFailure };

export function useGameModule(gameId: string): GameModuleState & { retry: () => void } {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<GameModuleState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    const loader = loadGame(gameId);
    if (!loader) {
      // Only reachable if this hook is used with an id isKnownGame rejected,
      // which is a caller bug, not a data-loading failure the player caused.
      setState({
        status: 'error',
        failure: { cause: 'data', detail: `no loader registered for ${gameId}` },
      });
      return;
    }

    loader
      .then((mod) => {
        if (cancelled) return;
        const result = validateGameIndex(mod.default);
        if (!result.ok) {
          setState({ status: 'error', failure: result.failure });
          return;
        }
        setState({ status: 'ready', game: mod.default, index: result.value });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // SHELL-024. The chunk failed to download or evaluate -- a network or
        // deployment problem, not a data problem, and the retry control this
        // powers is only useful if it is telling the truth about which.
        // PWA-034: offline specifically, not just "a network problem", when
        // the browser itself already knows there is no connection -- a more
        // actionable message than the generic one, and the only call site
        // that can actually be offline while this particular fetch fails
        // (see LoadCause's own header in platform/levels.ts).
        const cause: LoadCause = navigator.onLine ? 'network' : 'offline';
        setState({
          status: 'error',
          failure: { cause, detail: messageOf(error) },
        });
      });

    return () => {
      cancelled = true;
    };
  }, [gameId, attempt]);

  return {
    ...state,
    retry: () => {
      setAttempt((n) => n + 1);
    },
  };
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
